/**
 * The app root — the one place the session is checked and the stream is opened.
 *
 *   **One stream per tab.** Opened once here, never per panel, and only after the first reads have
 *   answered: the stream resumes from the lowest `X-Stream-Head` those reads carried, so nothing
 *   between a read and the stream is missed.
 *
 *   **A 401 anywhere routes to /signin in the matching state.** The console does not decide which
 *   state that is: `GET /api/auth` says, and the sign-in page renders the server's own sentence.
 */

import { useEffect } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useSession } from "./store/session";
import { useCompetition } from "./store/competition";
import { open as openStream, close as closeStream, onWake } from "./stream/connect";
import { useT } from "./i18n/useT";

export function App() {
  const { t } = useT();
  const who = useSession((s) => s.who);
  const checked = useSession((s) => s.checked);
  const check = useSession((s) => s.check);

  useEffect(() => {
    void check();
  }, [check]);

  useEffect(() => {
    if (!who) return;
    let alive = true;
    void useCompetition
      .getState()
      .loadAll()
      .then(() => {
        if (alive) openStream();
      });
    // A laptop closed for an hour and reopened: the browser's own reconnect handles the socket, and
    // this re-opens it if the browser gave up.
    const wake = () => {
      if (document.visibilityState === "visible") onWake();
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("online", wake);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("online", wake);
      closeStream();
    };
  }, [who]);

  // Neither signed in nor bounced yet. Not a state with a sentence: nothing has been claimed.
  if (!checked) {
    return (
      <div className="entry" role="status">
        <p className="entry__says">{t("ui.loading")}</p>
      </div>
    );
  }

  if (!who) return <Navigate to="/signin" replace />;

  return <Outlet />;
}
